import {
	configureStore,
	createListenerMiddleware,
	isRejectedWithValue,
	Middleware,
} from "@reduxjs/toolkit";

import { authReducer, authSlice, resetAuth, setAuth } from "@/pages/Public/redux/auth.slice";

import { companiesReducer, companiesSlice } from "@/pages/Private/redux/companies/companies.slice";

import {
	questionnaireReducer,
	questionnaireSlice,
} from "@/pages/Public/redux/questionnaire/questionnaire.slice";

import {
	companyToDoProgressReducer,
	companyToDoProgressSlice,
} from "@/pages/Private/redux/company-to-do-progress/company-to-do-progress.slice";

import { toDoReducer, toDoSlice } from "@/pages/Private/redux/to-do/to-do.slice";

import { industryReducer, industrySlice } from "@/pages/Private/redux/industry/industry.slice";

import {
	companySizeReducer,
	companySizeSlice,
} from "@/pages/Private/redux/company-size/company-size.slice";

import {
	companyRevenueReducer,
	companyRevenueSlice,
} from "@/pages/Private/redux/company-revenue/company-revenue.slice";

import {
	companyBalanceReducer,
	companyBalanceSlice,
} from "@/pages/Private/redux/company-balance/company-balance.slice";

import {
	tasksAndSubtasksReducer,
	tasksAndSubtasksSlice,
} from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";

import {
	taskAndSubtaskActivitySlice,
	tasksAndSubtasksActivityReducer,
} from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks-activity-slice";

import {
	notificationReducer,
	notificationSlice,
} from "@/pages/Private/redux/notification/notification.slice";

import { ROUTE_CONFIG } from "../routes/config";
import { allTags, api } from "./api";

const { LOGOUT, ERROR_PAGE } = ROUTE_CONFIG;

const authListenerMiddleware = createListenerMiddleware();

authListenerMiddleware.startListening({
	actionCreator: resetAuth,
	effect: (action, listenerApi) => {
		listenerApi.dispatch(api.util.invalidateTags(["Me"]));
		listenerApi.dispatch(api.util.invalidateTags(["Roles"]));
		listenerApi.dispatch(api.util.invalidateTags(allTags.filter((tag) => tag !== "Me")));
	},
});

authListenerMiddleware.startListening({
	actionCreator: setAuth,
	effect: (action, listenerApi) => {
		listenerApi.dispatch(api.util.invalidateTags(["Me"]));
		listenerApi.dispatch(api.util.invalidateTags(["Roles"]));
		listenerApi.dispatch(
			api.util.invalidateTags(allTags.filter((tag) => tag !== "Me" && tag !== "Roles"))
		);
	},
});

const rtkRejectQueryMiddleware: Middleware = () => (next) => (action) => {
	if (isRejectedWithValue(action)) {
		if (action.payload.status === 401) {
			if (
				!["login", "authTwoFactorCode", "verifyTwoFactor", "disableTwoFactor"].includes(
					action.meta.arg.endpointName
				)
			) {
				window.location.href = LOGOUT;
			}
		} else if (action.payload.status === 403) {
			window.location.href = ERROR_PAGE;
		}
	}

	return next(action);
};

export const store = configureStore({
	reducer: {
		[authSlice.name]: authReducer,
		[api.reducerPath]: api.reducer,
		[companiesSlice.name]: companiesReducer,
		[tasksAndSubtasksSlice.name]: tasksAndSubtasksReducer,
		[notificationSlice.name]: notificationReducer,
		[taskAndSubtaskActivitySlice.name]: tasksAndSubtasksActivityReducer,
		[questionnaireSlice.name]: questionnaireReducer,
		[companyToDoProgressSlice.name]: companyToDoProgressReducer,
		[toDoSlice.name]: toDoReducer,
		[industrySlice.name]: industryReducer,
		[companySizeSlice.name]: companySizeReducer,
		[companyRevenueSlice.name]: companyRevenueReducer,
		[companyBalanceSlice.name]: companyBalanceReducer,
	},
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({ serializableCheck: false })
			.concat(rtkRejectQueryMiddleware)
			.concat(authListenerMiddleware.middleware)
			.concat(api.middleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
